import { useState, type ReactNode } from 'react';
import { IconChevronLeft, IconCopy, IconDownload, IconLock, IconMail, IconPencil, IconPrinter, IconShare, IconTrash } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import './i18n';
import { Page } from '@hana/shell';
import { StateView, useToast, type ScreenState } from '@hana/ui';
import s from './viewerPhone.module.css';

// Mint-Files-Phone, khung 2: trình xem tệp điện thoại (giấy 342 · thanh Tệp / Gửi / PDF / Hỏi HANA · ngăn thao tác). Chỉ đọc: nút ghi báo toast.
const VP = [
  { head: 'BOOKING CONFIRMATION', rows: [['Booking No.', 'YMLU-SGN-884512'], ['Vessel', 'YM CERTAINTY 089N'], ['POL / POD', 'Cat Lai → Los Angeles'], ['Equipment', '2×40HC'], ['ETD', '24-Oct-2026'], ['SI cut-off', '22-Oct-2026 16:00']] },
  { head: 'EQUIPMENT RELEASE', rows: [['Depot', 'Cat Lai Depot'], ['Release No.', 'ER-884512-01'], ['Valid until', '25-Oct-2026']] },
] as const;
const TEXT = ['BOOKING CONFIRMATION', 'Booking No. YMLU-SGN-884512', 'Vessel YM CERTAINTY 089N', 'ETD 24-Oct-2026', 'SI cut-off 22-Oct-2026 16:00'];
type Sheet = 'file' | 'send' | 'pdf' | 'text';
interface Act { k: string; icon: ReactNode; danger?: boolean; dis?: string; text?: boolean }
const ic = (C: typeof IconDownload) => <C size={18} aria-hidden="true" />;
const SHEETS: Record<Exclude<Sheet, 'text'>, { title: string; items: Act[] }> = {
  file: { title: 'fv.m.file', items: [
    { k: 'fv.mi.dl', icon: ic(IconDownload) }, { k: 'fv.mi.share', icon: ic(IconShare) }, { k: 'fv.mi.link', icon: ic(IconCopy) },
    { k: 'fv.extract', icon: ic(IconPencil), text: true }, { k: 'fv.mi.print', icon: ic(IconPrinter) }, { k: 'fv.mi.del', icon: ic(IconTrash), danger: true },
  ] },
  send: { title: 'fv.m.send', items: [{ k: 'fv.mi.mailCust', icon: ic(IconMail) }, { k: 'fv.mi.relBkg', icon: ic(IconShare) }] },
  pdf: { title: 'fv.m.pdf', items: [
    { k: 'fv.mi.sign', icon: ic(IconPencil) },
    { k: 'fv.mi.pgOps', icon: ic(IconPencil), dis: 'fv.mi.ipad' },
    { k: 'fv.mi.txtOps', icon: ic(IconPencil), dis: 'fv.mi.ipad' },
  ] },
};

export function ViewerPhone({ state }: { state: ScreenState }) {
  const t = useT();
  const toast = useToast();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const noWrite = () => { setSheet(null); toast({ msg: t('fl.noWrite') }); };
  const blocked = state !== 'ready' && state !== 'empty';
  return (
    <Page>
      <header className={s.head}>
        <a className={s.back} href="/ezy/files" aria-label={t('fv.backFiles')}><IconChevronLeft size={20} aria-hidden="true" /></a>
        <div className={s.hx}>
          <div className={s.ht}>Booking Confirmation · YMLU-SGN-884512</div>
          <div className={s.hs}>BC_YMLU-SGN-884512.pdf · SE260639142 · Booking file</div>
        </div>
      </header>
      {state === 'loading' && <StateView state="loading" title={t('fv.loading')} />}
      {state === 'error' && <StateView state="error" title={t('fv.errT')} desc={t('fv.errPhone')} />}
      {state === 'noperm' && <StateView state="noperm" icon={<IconLock size={28} aria-hidden="true" />} title={t('fv.nopermPhone')} />}
      {!blocked && (
        <>
          <div className={s.pages}>
            {VP.map((p, i) => (
              <article key={p.head} className={s.paper} aria-label={`Trang ${i + 1}`}>
                <div className={s.pHead}>{p.head}</div>
                <div className={s.pRows}>{p.rows.map(([k, v]) => <><span key={`k${k}`} className={s.pk}>{k}</span><span key={`v${k}`} className={s.pv}>{v}</span></>)}</div>
                <div className={s.pFoot}>trang {i + 1}/{VP.length}</div>
              </article>
            ))}
          </div>
          <div className={s.bar}>
            {(['file', 'send', 'pdf'] as const).map((k) => (
              <button key={k} type="button" className={s.grp} onClick={() => setSheet(k)}>{t(SHEETS[k].title)}</button>
            ))}
            <button type="button" className={`${s.grp} ${s.ask}`} onClick={() => { setSheet('text'); toast({ msg: t('fv.phAsk') }); }}>{t('fv.askHana')}</button>
          </div>
        </>
      )}
      {sheet && (
        <>
          <div className={s.scrim} aria-hidden="true" onClick={() => setSheet(null)} />
          <div className={s.sheet} role="dialog" aria-modal="true" aria-labelledby="vpT">
            <div className={s.grab} />
            <h2 id="vpT" className={s.st}>{sheet === 'text' ? t('fv.extract') : t(SHEETS[sheet].title)}</h2>
            {sheet === 'text' ? (
              <>
                <div className={s.lines}>{TEXT.map((l) => <div key={l}>{l}</div>)}</div>
                <div className={s.copyRow}><button type="button" className={s.act} onClick={() => { setSheet(null); toast({ msg: t('fv.copied', { n: 5 }) }); }}>{ic(IconCopy)}{t('fv.copy')}</button></div>
              </>
            ) : SHEETS[sheet].items.map((a) => (
              <button key={a.k} type="button" className={`${s.item} ${a.danger ? s.danger : ''}`} disabled={!!a.dis} onClick={() => (a.text ? setSheet('text') : noWrite())}>
                {a.icon}<span className={s.it}>{t(a.k)}</span>{a.dis && <span className={s.dis}>{t(a.dis)}</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </Page>
  );
}
